import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Clapperboard, Plus, Loader2, PlayCircle, Settings, Sparkles, ExternalLink, UploadCloud } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import CreatorHeroStats from "@/components/creator/CreatorHeroStats";
import CreatorAnalytics from "@/components/creator/CreatorAnalytics";
import CreatorQuickActions from "@/components/creator/CreatorQuickActions";
import CreatorActivityFeed from "@/components/creator/CreatorActivityFeed";
import CreatorChallengesSection from "@/components/creator/CreatorChallengesSection";
import CreatorContactsSection from "@/components/creator/CreatorContactsSection";
import CreateQuizModal from "@/components/creator/CreateQuizModal";
import CreateAssignmentModal from "@/components/creator/CreateAssignmentModal";
import IssueCertificateModal from "@/components/creator/IssueCertificateModal";
import AIMarketingModal from "@/components/creator/AIMarketingModal";
import CreateChallengeModal from "@/components/creator/CreateChallengeModal";

export default function CreatorStudioPage() {
  const [profile, setProfile] = useState(null);
  const [courses, setCourses] = useState([]);
  const [challenges, setChallenges] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [becomingCreator, setBecomingCreator] = useState(false);
  const [handle, setHandle] = useState("");
  const [bio, setBio] = useState("");
  const [newCourse, setNewCourse] = useState({ title: "", description: "", price: "" });
  const [activeModal, setActiveModal] = useState(null);

  const loadAll = async () => {
    const p = await api.get("/creator/me");
    setProfile(p.data);
    setHandle(p.data.creator_handle || "");
    setBio(p.data.creator_bio || "");
    if (p.data.is_creator) {
      const [c, a, ch] = await Promise.all([api.get("/courses/my"), api.get("/creator/analytics"), api.get("/custom-challenges/my")]);
      setCourses(c.data);
      setAnalytics(a.data);
      setChallenges(ch.data.created || []);
    }
  };

  useEffect(() => {
    setLoading(true);
    loadAll().finally(() => setLoading(false));
  }, []);

  const becomeCreator = async () => {
    setBecomingCreator(true);
    try {
      await api.post("/creator/become", { handle, bio });
      toast.success("You're now a creator!");
      await loadAll();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBecomingCreator(false);
    }
  };

  const createCourse = async () => {
    if (!newCourse.title.trim()) { toast.error("Title is required"); return; }
    setCreating(true);
    try {
      const { data } = await api.post("/courses", { ...newCourse, price: Number(newCourse.price) || 0 });
      toast.success("Course created — now add videos!");
      setShowCreate(false);
      setNewCourse({ title: "", description: "", price: "" });
      setCourses((prev) => [data, ...prev]);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setCreating(false);
    }
  };

  const handleQuickAction = (id) => {
    if (id === "create-course") { setShowCreate(true); return; }
    if (id === "create-challenge") { setActiveModal(id); return; }
    if (id === "upload-video" || id === "generate-notes" || id === "manage-resources" || id === "configure-boss-battle") {
      if (courses.length === 0) { toast.error("Create a course first"); setShowCreate(true); return; }
      window.location.href = `/creator/studio/${courses[0].id}`;
      return;
    }
    if (courses.length === 0) { toast.error("Create a course first"); setShowCreate(true); return; }
    setActiveModal(id);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="creator-studio-loading">
        <div className="w-10 h-10 border-2 border-accent border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!profile.is_creator) {
    return (
      <div className="max-w-lg mx-auto px-4 sm:px-6 py-16" data-testid="creator-studio-onboarding">
        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-2xl bg-pink-400/10 border border-pink-400/30 flex items-center justify-center mx-auto mb-4">
            <Clapperboard className="w-8 h-8 text-pink-400" />
          </div>
          <h1 className="font-heading text-3xl font-semibold text-white">Become a Creator</h1>
          <p className="text-sm text-slate-400 mt-2">Upload your own course videos — AI auto-generates Udemy-style notes from your voice. Earn from every sale.</p>
        </div>
        <div className="glass-panel rounded-2xl p-6 space-y-4">
          <div>
            <label className="text-xs font-semibold text-slate-400 mb-1.5 block">Your Public Handle</label>
            <div className="flex items-center gap-1">
              <span className="text-sm text-slate-500">cloudivo.ai/creator/</span>
              <input value={handle} onChange={(e) => setHandle(e.target.value.toLowerCase())} placeholder="your-name" data-testid="creator-onboarding-handle-input"
                className="flex-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-pink-400/50" />
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-400 mb-1.5 block">Bio</label>
            <textarea value={bio} onChange={(e) => setBio(e.target.value)} rows={3} placeholder="Tell learners about yourself..." data-testid="creator-onboarding-bio-input"
              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-pink-400/50 resize-none" />
          </div>
          <button onClick={becomeCreator} disabled={becomingCreator} data-testid="creator-onboarding-submit-btn"
            className="w-full flex items-center justify-center gap-2 bg-pink-500 hover:bg-pink-400 disabled:opacity-60 text-white font-bold text-sm py-3 rounded-xl transition-colors">
            {becomingCreator ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            Become a Creator
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 space-y-10" data-testid="creator-studio-page">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-1">
            <Clapperboard className="w-5 h-5 text-[#A855F7]" />
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#A855F7]">Creator Studio</span>
          </div>
          <CreatorHeroStats profile={profile} analytics={analytics} />
          <Link to={`/creator/${profile.creator_handle}`} data-testid="creator-studio-view-profile-link" className="inline-flex items-center gap-1 text-xs text-slate-400 hover:text-white mt-4">
            View public profile @{profile.creator_handle} <ExternalLink className="w-3 h-3" />
          </Link>
        </div>
        <button onClick={() => setShowCreate(true)} data-testid="creator-studio-new-course-btn"
          className="flex items-center gap-2 bg-gradient-to-r from-[#3B82F6] to-[#A855F7] hover:opacity-90 text-white font-semibold text-sm px-4 py-2.5 rounded-lg transition-opacity shrink-0">
          <Plus className="w-4 h-4" /> New Course
        </button>
      </div>

      {showCreate && (
        <div className="glass-panel rounded-2xl p-6" data-testid="creator-studio-create-course-form">
          <h3 className="font-heading text-base font-semibold text-white mb-4">Create a New Course</h3>
          <div className="space-y-3">
            <input value={newCourse.title} onChange={(e) => setNewCourse({ ...newCourse, title: e.target.value })} placeholder="Course title" data-testid="create-course-title-input"
              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white outline-none focus:border-primary/50" />
            <textarea value={newCourse.description} onChange={(e) => setNewCourse({ ...newCourse, description: e.target.value })} placeholder="Description" rows={3} data-testid="create-course-description-input"
              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white outline-none focus:border-primary/50 resize-none" />
            <div>
              <label className="text-xs font-semibold text-slate-400 mb-1.5 block">Price (₹, 0 for free)</label>
              <input type="number" min="0" value={newCourse.price} onChange={(e) => setNewCourse({ ...newCourse, price: e.target.value })} placeholder="499" data-testid="create-course-price-input"
                className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white outline-none focus:border-primary/50" />
            </div>
            <div className="flex items-center gap-3 pt-1">
              <button onClick={createCourse} disabled={creating} data-testid="create-course-submit-btn"
                className="flex items-center gap-2 bg-primary hover:bg-primary/90 disabled:opacity-60 text-white font-semibold text-sm px-4 py-2.5 rounded-lg transition-colors">
                {creating && <Loader2 className="w-4 h-4 animate-spin" />} Create Course
              </button>
              <button onClick={() => setShowCreate(false)} data-testid="create-course-cancel-btn" className="text-sm text-slate-400 hover:text-white px-4 py-2.5">Cancel</button>
            </div>
          </div>
        </div>
      )}

      <CreatorAnalytics analytics={analytics} />

      <CreatorContactsSection />
      <CreatorQuickActions hasCourses={courses.length > 0} onAction={handleQuickAction} />
      <CreatorActivityFeed analytics={analytics} />

      <div>
        <h2 className="text-xl sm:text-2xl font-medium text-[#f7f8f8] mb-4">Your Courses</h2>
        {courses.length === 0 ? (
          <div className="glass-panel rounded-2xl p-10 text-center" data-testid="creator-studio-no-courses">
            <PlayCircle className="w-9 h-9 text-slate-500 mx-auto mb-3" />
            <p className="text-slate-400 text-sm">You haven't created any courses yet.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {courses.map((c) => (
              <Link key={c.id} to={`/creator/studio/${c.id}`} data-testid={`creator-studio-course-row-${c.id}`}
                className="rounded-xl border border-white/[0.08] bg-[#131a29]/80 backdrop-blur-xl p-4 flex items-center justify-between gap-4 hover:border-white/20 hover:-translate-y-0.5 transition-[transform,border-color] duration-200">
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold text-white text-sm">{c.title}</h3>
                    <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${c.status === "published" ? "bg-emerald-400/10 text-emerald-300" : "bg-slate-400/10 text-slate-400"}`}>{c.status}</span>
                  </div>
                  <p className="text-xs text-slate-500 mt-1">{c.video_ids?.length || 0} video{c.video_ids?.length === 1 ? "" : "s"} · {c.price > 0 ? `₹${c.price.toLocaleString("en-IN")}` : "Free"}</p>
                </div>
                <div className="flex items-center gap-2 text-slate-400">
                  <UploadCloud className="w-4 h-4" />
                  <Settings className="w-4 h-4" />
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>

      <CreatorChallengesSection challenges={challenges} />

      {activeModal === "create-quiz" && <CreateQuizModal courses={courses} onClose={() => setActiveModal(null)} onCreated={loadAll} />}
      {activeModal === "create-assignment" && <CreateAssignmentModal courses={courses} onClose={() => setActiveModal(null)} onCreated={loadAll} />}
      {activeModal === "issue-certificate" && <IssueCertificateModal courses={courses} onClose={() => setActiveModal(null)} />}
      {activeModal === "ai-marketing" && <AIMarketingModal courses={courses} onClose={() => setActiveModal(null)} />}
      {activeModal === "create-challenge" && <CreateChallengeModal onClose={() => setActiveModal(null)} />}
    </div>
  );
}
